'use client';
import React, { useState, useEffect, useCallback } from 'react';
import GXGLayout from '@/components/GXGLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logActivity } from '@/lib/activityLogger';

const CoinIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="#f59e0b"><circle cx="12" cy="12" r="10"/></svg>;

const staticRewards = [
  { id: 'r1', name: 'Legendary Cartridge', desc: 'Ultra rare. For true legends.', coins: 5000, rarity: 'EXCLUSIVE', rarityColor: 'badge-gold', icon: '📼' },
  { id: 'r2', name: 'Retro Night Ticket', desc: 'Join the next Retro Night event', coins: 1000, rarity: 'RARE', rarityColor: 'badge-blue', icon: '🎟️' },
  { id: 'r3', name: 'GXG Sticker Pack', desc: 'Show off your GXG pride', coins: 500, rarity: 'COMMON', rarityColor: 'badge-purple', icon: '🎨' },
  { id: 'r4', name: '8-Bit Hoodie', desc: 'Premium GXG merch, pixel art design', coins: 2500, rarity: 'EPIC', rarityColor: 'badge-pink', icon: '👕' },
  { id: 'r5', name: 'GXG Champion Pin', desc: 'Limited edition enamel pin', coins: 750, rarity: 'RARE', rarityColor: 'badge-blue', icon: '📌' },
  { id: 'r6', name: 'Speed Runner Badge', desc: 'For the fastest players', coins: 300, rarity: 'COMMON', rarityColor: 'badge-purple', icon: '⚡' },
  { id: 'r7', name: 'GXG Cap', desc: 'Wear your rank with pride', coins: 1500, rarity: 'RARE', rarityColor: 'badge-blue', icon: '🧢' },
  { id: 'r8', name: 'Retro Controller', desc: 'Vintage style custom controller', coins: 3250, rarity: 'EPIC', rarityColor: 'badge-pink', icon: '🎮' },
];

interface RedemptionHistory {
  id: string;
  reward_title: string;
  coins_spent: number;
  status: string;
  created_at: string;
}

const categories = ['All', 'Items', 'Badges', 'Experiences', 'Exclusive'];

export default function RewardsPage() {
  const { profile, refreshProfile, user } = useAuth();
  const [activeCategory, setActiveCategory] = useState('All');
  const [redeemed, setRedeemed] = useState<string[]>([]);
  const [showSuccess, setShowSuccess] = useState<string | null>(null);
  const [showError, setShowError] = useState<string | null>(null);
  const [redemptionHistory, setRedemptionHistory] = useState<RedemptionHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const coinBalance = profile?.gxg_coins ?? 0;

  const loadHistory = useCallback(async () => {
    if (!user?.id) { setHistoryLoading(false); return; }
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('coin_redemptions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!error && data) {
        setRedemptionHistory(data);
      }
    } catch (err) {
      console.error('Load history error:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleRedeem = async (rewardId: string, rewardName: string, coinCost: number) => {
    if (!user?.id) {
      setShowError(rewardId);
      setTimeout(() => setShowError(null), 2000);
      return;
    }
    if (coinBalance < coinCost) return;

    try {
      const supabase = createClient();
      const { data, error } = await supabase.rpc('redeem_coins', {
        p_user_id: user.id,
        p_reward_id: null,
        p_reward_title: rewardName,
        p_coins_spent: coinCost,
      });

      if (error || !data?.success) {
        console.error('Redeem error:', error || data?.error);
        setShowError(rewardId);
        setTimeout(() => setShowError(null), 2000);
        return;
      }

      await logActivity({
        action: 'redeem_reward',
        entityType: 'reward',
        details: { rewardName, coinsSpent: coinCost, newBalance: data.new_balance },
      });

      setRedeemed(prev => [...prev, rewardId]);
      setShowSuccess(rewardId);
      setTimeout(() => setShowSuccess(null), 2000);

      // Refresh profile to get updated coin balance
      await refreshProfile();
      loadHistory();
    } catch (err) {
      console.error('Redeem error:', err);
      setShowError(rewardId);
      setTimeout(() => setShowError(null), 2000);
    }
  };

  const filteredRewards = staticRewards.filter(r => {
    if (activeCategory === 'All') return true;
    if (activeCategory === 'Badges') return r.name.includes('Badge');
    if (activeCategory === 'Exclusive') return r.rarity === 'EXCLUSIVE';
    return true;
  });

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <GXGLayout>
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display font-black text-3xl md:text-4xl text-white mb-2">
              Rewards <span className="gxg-gradient-text">Store</span>
            </h1>
            <p className="text-white/50 text-sm">Spend your GXG coins on exclusive items, badges, and experiences.</p>
          </div>
          <div className="glass-card px-6 py-4 flex items-center gap-3">
            <CoinIcon />
            <div>
              <div className="text-[10px] text-white/40 uppercase tracking-wider font-mono">Your Balance</div>
              <div className="font-display font-bold text-2xl text-gxg-gold">
                {coinBalance.toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        {!user && (
          <div className="mb-6 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-sm">
            Sign in to redeem rewards and track your coin balance.
          </div>
        )}

        {/* Categories */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
                activeCategory === cat
                  ? 'bg-purple-500/20 text-gxg-purple2 border border-purple-500/30' :'text-white/50 hover:text-white/80 border border-white/8 hover:border-white/15'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Rewards Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          {filteredRewards.map(reward => {
            const canAfford = coinBalance >= reward.coins;
            const isRedeemed = redeemed.includes(reward.id);
            return (
              <div key={reward.id} className={`game-card relative ${!canAfford ? 'opacity-70' : ''}`}>
                {showSuccess === reward.id && (
                  <div className="absolute inset-0 bg-green-500/20 border border-green-500/40 rounded-xl z-10 flex items-center justify-center">
                    <div className="text-gxg-green font-bold text-sm">✓ Redeemed!</div>
                  </div>
                )}
                {showError === reward.id && (
                  <div className="absolute inset-0 bg-red-500/20 border border-red-500/40 rounded-xl z-10 flex items-center justify-center">
                    <div className="text-red-400 font-bold text-sm text-center px-2">
                      {!user ? 'Sign in first' : 'Insufficient coins'}
                    </div>
                  </div>
                )}
                <div className="h-32 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center relative">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="text-4xl relative z-10">{reward.icon}</span>
                  <span className={`absolute top-2 right-2 ${reward.rarityColor}`}>{reward.rarity}</span>
                </div>
                <div className="p-3">
                  <h3 className="font-semibold text-sm text-white mb-1">{reward.name}</h3>
                  <p className="text-xs text-white/45 mb-3">{reward.desc}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-gxg-gold text-sm font-mono font-semibold">
                      <CoinIcon /> {reward.coins.toLocaleString()}
                    </div>
                    <button
                      onClick={() => handleRedeem(reward.id, reward.name, reward.coins)}
                      disabled={!canAfford || isRedeemed}
                      className={`text-xs px-3 py-1.5 rounded-lg transition-all ${
                        isRedeemed
                          ? 'bg-green-500/20 text-gxg-green border border-green-500/30 cursor-default'
                          : canAfford
                          ? 'btn-gxg-primary' :'bg-white/5 text-white/30 cursor-not-allowed border border-white/8'
                      }`}
                    >
                      {isRedeemed ? '✓ Done' : canAfford ? 'Redeem' : 'Need More'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Redemption History */}
        <div className="glass-card p-5">
          <h2 className="font-display font-bold text-base text-white mb-4">Redemption History</h2>
          {historyLoading ? (
            <div className="flex items-center gap-2 text-white/40 text-sm py-4">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
              Loading history...
            </div>
          ) : redemptionHistory.length === 0 ? (
            <div className="text-white/30 text-sm py-4 text-center">No redemptions yet. Redeem your first reward above!</div>
          ) : (
            <div className="space-y-2">
              {redemptionHistory.map((item) => (
                <div key={item.id} className="flex items-center gap-4 p-3 rounded-lg hover:bg-white/3 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-sm">🎁</div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-white/90">{item.reward_title}</div>
                    <div className="text-xs text-white/40">{formatDate(item.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-1 text-gxg-gold text-sm font-mono">
                    <CoinIcon /> {item.coins_spent.toLocaleString()}
                  </div>
                  <span className={`text-xs font-semibold ${item.status === 'completed' ? 'text-gxg-green' : 'text-gxg-blue2'}`}>
                    {item.status === 'completed' ? 'Redeemed' : item.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </GXGLayout>
  );
}
